import { filter } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'

import * as commonActions from 'common/actions'
import { CreditOffer } from 'embed/constants/models'

import ErrorIndicator from 'embed/components/ErrorIndicator'
import LoadingIndicator, { BRAND_COLOR } from 'embed/components/LoadingIndicator'
import { creditFlowSelector } from 'embed/selectors/config'
import { filtersCreditRewardsPreferenceSelector } from 'embed/selectors/filters'
import { loadingErrorSelector, loadingSelector } from 'embed/selectors/loading'
import { creditCardRatesSortDirectionSelector, creditCardRatesSortOptionSelector } from 'embed/selectors/rates'
import { MultiProductState } from 'embed/reducers'
import { uiSelectedCreditStepSelector } from 'embed/selectors/ui'
import {
  CreditCardFlow,
  CreditCardStep,
  CreditSortOptionUI,
  Filter,
  Product,
  ProductResponseKeys,
  QueryParamKeys,
  SortDirection,
} from 'even-ts-static'
import { CreditOfferList } from './index'

interface OwnProps {
  allCreditCardOffers?: CreditOffer[]
  applyNow?: boolean
  creditOffers?: CreditOffer[]
  filteredCreditCardOffers?: CreditOffer[]
  handleOfferCta: (offer: CreditOffer) => void
  handleOfferCtaSecondary?: (offer: CreditOffer) => void
  leadUuid?: string
  limit?: number
  loadingPredicate?: boolean
  offerListRef?: React.Ref<HTMLOListElement>
  rateTableUuid?: string
  rewardsEnabled?: boolean
  showRewardsInfo?: boolean
  hideSorting?: boolean
  toggleSpendingProfile?: (visible: boolean) => void
}

interface StoreProps {
  creditOffers: CreditOffer[]
  error: boolean
  flow: CreditCardFlow
  genericOfferIndex: number
  loading: boolean
  rewardsPreference: string
  sortDirection: SortDirection
  sortOption: CreditSortOptionUI
  step: CreditCardStep
}

interface DispatchProps {
  dispatchSortChanged: (sortOption: CreditSortOptionUI, sortDirection: SortDirection) => null
}

type Props = OwnProps & DispatchProps & StoreProps

const CreditOfferListContainer: React.FC<Props> = ({
  allCreditCardOffers,
  dispatchSortChanged,
  filteredCreditCardOffers,
  handleOfferCta,
  handleOfferCtaSecondary,
  error,
  loading,
  rewardsEnabled,
  sortDirection,
  sortOption,
  ...other
}) => {
  const onSortOptionClick = React.useCallback(
    (updatedSortOption: CreditSortOptionUI) => {
      if (updatedSortOption === sortOption) {
        // If they clicked the same sort, reverse it
        const updatedSortDirection = sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc
        dispatchSortChanged(updatedSortOption, updatedSortDirection)
      } else {
        dispatchSortChanged(updatedSortOption, SortDirection.Asc)
      }
    },
    [dispatchSortChanged, sortOption],
  )

  if (error) {
    return <ErrorIndicator />
  } else if (loading) {
    return (
      <LoadingIndicator
        brandColor={BRAND_COLOR.TRUE}
        loadingLabel="Finding Credit Card Offers"
        product={Product.CreditCard}
      />
    )
  } else {
    return (
      <CreditOfferList
        allCreditCardOffers={allCreditCardOffers}
        filteredCreditCardOffers={filteredCreditCardOffers}
        dispatchCreditCardOfferCta={handleOfferCta}
        dispatchCreditCardOfferCtaSecondary={handleOfferCtaSecondary}
        onSortOptionClick={onSortOptionClick}
        rewardsEnabled={rewardsEnabled}
        sort={sortOption}
        {...other}
      />
    )
  }
}

const mapStateToProps = (state: MultiProductState, ownProps: OwnProps): StoreProps => {
  const issuerFilter = state.filters[Product.CreditCard]?.[Filter.Issuer] ?? ''
  const step = uiSelectedCreditStepSelector(state)

  let creditOffers
  if (step === CreditCardStep.Preview) {
    creditOffers = ownProps.creditOffers
  } else {
    creditOffers = ownProps.filteredCreditCardOffers
  }
  if (!creditOffers) {
    creditOffers = state.rates.sorted[Product.CreditCard]?.[`${ProductResponseKeys[Product.CreditCard]}Offers`] ?? []
  }
  if (issuerFilter !== '') {
    creditOffers = filter(creditOffers, (offer: CreditOffer) => offer.partner?.uuid === issuerFilter)
  }

  return {
    creditOffers,
    error: loadingErrorSelector(state, Product.CreditCard),
    flow: creditFlowSelector(state),
    genericOfferIndex: state.config[QueryParamKeys.GenericOfferIndex],
    loading: !!(loadingSelector(state, Product.CreditCard) || ownProps.loadingPredicate), // intended to be || not ??
    rewardsPreference: filtersCreditRewardsPreferenceSelector(state),
    sortDirection: creditCardRatesSortDirectionSelector(state),
    sortOption: creditCardRatesSortOptionSelector(state),
    step,
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSortChanged: (sortOption, sortDirection) =>
    dispatch(commonActions.sortChanged(sortDirection, Product.CreditCard, sortOption)),
})

export const ConnectedCreditOfferListContainer = connect(mapStateToProps, mapDispatchToProps)(CreditOfferListContainer)
